import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

function URSGrid({ ideaId }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const fetchURS = async () => {
      try {
        const res = await fetch(`/api/linked-urs-by-idea/${ideaId}`);
        const json = await res.json();
        setRows(json);
      } catch (err) {
        console.error("Failed to load linked URS", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchURS();
  }, [ideaId]);

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "reference_code", headerName: "Ref", width: 130 },
    { field: "title", headerName: "Title", width: 250 },
    { field: "status", headerName: "Status", width: 120 },
    { field: "priority", headerName: "Priority", width: 100 },
    { field: "roi_potential", headerName: "ROI", width: 100 },
  ];

  return (
    <Box sx={{ height: 300 }}>
      {loading ? (
        <CircularProgress size={24} />
      ) : rows.length === 0 ? (
        <Typography>No linked URS entries.</Typography>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          getRowId={(row) => row.id}
        />
      )}
    </Box>
  );
}

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport csvOptions={{ fileName: "change_idea_log" }} />
    </GridToolbarContainer>
  );
}

export default function ChangeIdeaLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedIdeaId, setSelectedIdeaId] = useState(null);
  const [selectedIdeaTitle, setSelectedIdeaTitle] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch("/api/change-ideas");
        const json = await res.json();
        setRows(json);
      } catch (err) {
        console.error("Failed to load change idea log", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleViewClick = (idea) => {
    setSelectedIdeaId(idea.id);
    setSelectedIdeaTitle(idea.idea_title);
    setOpenDialog(true);
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "idea_title", headerName: "Idea Title", width: 280 },
    {
      field: "description",
      headerName: "Description",
      width: 400,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      ),
    },
    { field: "priority", headerName: "Priority", width: 100 },
    { field: "roi_potential", headerName: "ROI", width: 100 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "actions",
      type: "actions",
      width: 80,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="View"
          onClick={() => handleViewClick(params.row)}
        />,
      ],
    },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Change Idea Log
      </Typography>
      <Box sx={{ height: 700, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10, 25]}
          components={{ Toolbar: CustomToolbar }}
          getRowId={(row) => row.id}
        />
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Linked URS for: {selectedIdeaTitle}</DialogTitle>
        <DialogContent>
          <URSGrid ideaId={selectedIdeaId} />
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
